type ApiError = Error & { status?: number; body?: any };

// Alles wat we als JSON kunnen sturen
type JsonValue =
    | string
    | number
    | boolean
    | null
    | JsonValue[]
    | { [key: string]: JsonValue };

// Onze init: RequestInit maar body mag óók JsonValue/FormData/Blob/etc
type ApiInit = Omit<RequestInit, "body"> & {
    body?: BodyInit | JsonValue | Record<string, any>;
};

export function createApi(apiBase: string) {
    return async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
        const headers = new Headers(init.headers);
        // Onschadelijk in productie (geen ngrok daar) — voorkomt alleen dat een lokale
        // ngrok-tunnel (voor Mollie-webhook-tests) zijn gratis-tier waarschuwingspagina
        // teruggeeft i.p.v. het echte antwoord, wat de browser anders als CORS-fout
        // blokkeert (de waarschuwingspagina zelf stuurt geen CORS-headers mee).
        headers.set("ngrok-skip-browser-warning", "true");

        let body: BodyInit | undefined;

        // Als body een plain object is -> JSON
        if (
            init.body !== undefined &&
            typeof init.body === "object" &&
            !(init.body instanceof FormData) &&
            !(init.body instanceof Blob) &&
            !(init.body instanceof ArrayBuffer) &&
            !(init.body instanceof URLSearchParams)
        ) {
            headers.set("Content-Type", "application/json");
            body = JSON.stringify(init.body);
        } else {
            body = init.body as BodyInit | undefined;
        }

        const res = await fetch(`${apiBase}${path}`, {
            ...init,
            headers,
            body,
        });

        const contentType = res.headers.get("content-type") ?? "";
        const isJson = contentType.includes("application/json");

        const data = isJson
            ? await res.json().catch(() => null)
            : await res.text().catch(() => null);

        if (!res.ok) {
            const err: ApiError = new Error((data && (data.message || data.error)) || res.statusText);
            err.status = res.status;
            err.body = data;
            throw err;
        }

        return data as T;
    };
}
